import { Form, Input, Button, message, Row, Col, Radio } from "antd";
import DateTimePicker from "../../components/DateTimePicker";
import { EventData, venueType } from "../../models/event";
import VenuConfirmationEvent from "./venuconfirmation.event";
import DescriptionArea from "../../components/DescriptionArea";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { IconCheck, IconArrowNarrowLeft } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router";
import { ROUTES } from "../../constants/routes.constant";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { useAPI } from "../../hooks/useAPI";
import {
  getCall,
  postCall,
  updateCall,
} from "../../constants/apicalls.constant";
import Venue from "./venue.event";
import {
  getTbdCityLockedInStorage,
  hasFullVenueAddress,
  isYetToBeAnnounced,
  mergeVenueFromSave,
  resolveLockedTbdCity,
  resolveVenueFromEvent,
  setTbdCityLockedInStorage,
  syncVenueFormFields,
} from "../../utils/venueHelpers";
import Loader from "../../components/Loader";
import { setSelectedEventList } from "../../store/slices/event.slice";

import {
  convertToDayjsForForm,
  formatAllDatesForSubmit,
} from "../../utils/DateAndTimeConvertor";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";

import { checkForValidation } from "../../store/slices/validation.slice";
import {
  EnvironmentOutlined,
  GlobalOutlined,
  MergeCellsOutlined,
} from "@ant-design/icons";

import SectionCard from "../../components/SectionCard";

import { transformImageFieldsToUIDs } from "../../utils/TransfromImageFieldsToUIDs";
import { logged } from "../../store/slices/user.slice";

import {
  canEditEventSetup,
  canEditScheduleAndVenue,
  isScheduleAndVenueLocked,
} from "../../utils/eventEditAccess";
import { EventMode } from "./types";

const buildTbdVenuePayload = (
  city: string,
  line1?: string,
  line2?: string,
  venue_name?: string,
  existingVenue?: import("../../models/event").venueType | null,
): import("../../models/event").venueType => ({
  name: venue_name || city,
  city,
  place_id: existingVenue?.place_id || "",
  lat: existingVenue?.lat || "",
  lng: existingVenue?.lng || "",
  line1: line1 || "",
  line2: line2 || "",
  state: existingVenue?.state || "",
  postal: existingVenue?.postal || "",
  type: existingVenue?.type || "tbd",
  country: existingVenue?.country || "",
});

// ─── Event mode options ───────────────────────────────────────────────────────
const EVENT_MODES: EventMode[] = [
  {
    key: "on_ground",
    label: "On Ground",
    description: "Physical venue",
    icon: <EnvironmentOutlined style={{ fontSize: 20 }} />,
    color: "#f97316",
    bg: "#fff7ed",
  },
  {
    key: "virtual",
    label: "Virtual",
    description: "Online event",
    icon: <GlobalOutlined style={{ fontSize: 20 }} />,
    color: "#8b5cf6",
    bg: "#f5f3ff",
  },
  {
    key: "hybrid",
    label: "Hybrid",
    description: "On ground + Online",
    icon: <MergeCellsOutlined style={{ fontSize: 20 }} />,
    color: "#0ea5e9",
    bg: "#f0f9ff",
  },
];

// ─── Event mode card selector ─────────────────────────────────────────────────
const EventModeSelector = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (key: string) => void;
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 10,
    }}
  >
    {EVENT_MODES.map((mode) => {
      const selected = value === mode.key;
      const { event: selectedEvent } = useSelector((state: RootState) => ({
        event: state.event,
      }));
      return (
        <button
          key={mode.key}
          type="button"
          onClick={() => onChange(mode.key)}
          style={{
            border: `1.5px solid ${selected ? "#021526" : "#e5e7eb"}`,
            background: selected ? "#fff" : "#fafafa",
            borderRadius: 8,
            padding: "12px 8px 10px",
            cursor: canEditEventSetup(selectedEvent)
              ? "pointer"
              : "not-allowed",
            textAlign: "center",
            transition: "all 0.15s ease",
            position: "relative",
            outline: "none",
          }}
          disabled={!canEditEventSetup(selectedEvent)}
        >
          {selected && (
            <span
              style={{
                position: "absolute",
                top: 5,
                right: 5,
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "#021526",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheck size={10} color="#fff" strokeWidth={3} />
            </span>
          )}
          <div
            style={{
              color: selected ? "#021526" : "#9ca3af",
              marginBottom: 5,
            }}
          >
            {mode.icon}
          </div>
          <div
            style={{
              fontWeight: selected ? 700 : 500,
              fontSize: 12,
              color: selected ? "#021526" : "#374151",
              lineHeight: 1.2,
            }}
          >
            {mode.label}
          </div>
          <div
            style={{
              fontSize: 11,
              color: selected ? "#021526" : "#9ca3af",
              marginTop: 2,
            }}
          >
            {mode.description}
          </div>
        </button>
      );
    })}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const AddEvents = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [eventType, setEventType] = useState("on_ground");
  const [venue, setVenue] = useState<venueType | null>(null);
  const [venueTbd, setVenueTbd] = useState(false);
  const [tbdCity, setTbdCity] = useState<string | null>(null);
  const skipNavigate = useRef(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const hydratedEventIdRef = useRef<string | null>(null);

  const { event: selectedEvent, user } = useSelector((state: RootState) => ({
    event: state.event,
    user: state.user,
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const canEdit = canEditEventSetup(selectedEvent);
  const scheduleAndVenueLocked = isScheduleAndVenueLocked(selectedEvent);
  const canEditScheduleVenue = canEditScheduleAndVenue(selectedEvent);
  const isRequested = selectedEvent?.status === "requested";
  const isFormDisabled = !canEdit && !canEditScheduleVenue;

  const { loading, error, trigger } = useAPI<{
    status: boolean;
    message: string;
    data: EventData;
  }>(ENDPOINTS.event);

  const {
    response: fetchedEvent,
    loading: fetchingEvent,
    error: fetchError,
    trigger: fetchEventTrigger,
  } = useAPI<{
    status: boolean;
    message: string;
    data: EventData;
  }>(ENDPOINTS.event);

  useEffect(() => {
    if (id && (!selectedEvent || !selectedEvent.id)) {
      setFetchLoading(true);
      fetchEventTrigger(getCall({}, id)).finally(() => setFetchLoading(false));
    }
  }, [id, selectedEvent, fetchEventTrigger]);

  const isVirtual = eventType === "virtual";

  const venueLocked = isRequested || scheduleAndVenueLocked;

  const onSubmit = async (
    values: EventData & {
      line1: string;
      line2: string;
      venue_name: string;
      city?: string;
    },
  ) => {
    const shouldNavigate = !skipNavigate.current;
    skipNavigate.current = false;

    const { line1, line2, venue_name, city, ...restData } = values;

    const convertedValues = formatAllDatesForSubmit<EventData>(
      id ? { ...selectedEvent, ...restData } : restData,
    );

    let valueToSend: EventData = { ...convertedValues };

    if (!isVirtual) {
      const resolvedCity = tbdCity || city || venue?.city || "";

      if (tbdCity) {
        valueToSend.yet_to_be_announced = !hasFullVenueAddress(venue);
        valueToSend.venue = buildTbdVenuePayload(
          tbdCity,
          line1,
          line2,
          venue_name,
          venue,
        );
      } else if (venueTbd) {
        if (!resolvedCity) {
          message.error("Please select a city for your event.");
          return;
        }
        valueToSend.yet_to_be_announced = true;
        valueToSend.venue = buildTbdVenuePayload(
          resolvedCity,
          line1,
          line2,
          venue_name,
          venue,
        );
      } else if (venue) {
        valueToSend.yet_to_be_announced = false;
        valueToSend.venue = {
          ...venue,
          name: venue_name,
          line1,
          line2,
          city: resolvedCity,
        };
      } else {
        message.error(
          "Please select a venue or choose 'Will think of a venue later'",
        );
        return;
      }
    }

    if (id) valueToSend = transformImageFieldsToUIDs<EventData>(valueToSend);
    if (!id) valueToSend.status = "draft";

    if (valueToSend.steps_info) {
      if (!valueToSend.steps_info.includes("basic_info")) {
        valueToSend.steps_info = ["basic_info", ...valueToSend.steps_info];
      }
    } else {
      valueToSend.steps_info = ["basic_info"];
    }

    const res = id
      ? await trigger(updateCall<EventData>(id, valueToSend))
      : await trigger(postCall<EventData>(valueToSend));

    if (res?.status) {
      if (!isVirtual) {
        const mergedVenue = mergeVenueFromSave(
          res.data.venue,
          valueToSend.venue,
        );
        const yetTbd = isYetToBeAnnounced(valueToSend.yet_to_be_announced);
        const lockedCity =
          tbdCity || mergedVenue?.city || getTbdCityLockedInStorage(id);

        if (mergedVenue) {
          setVenue(mergedVenue);
          syncVenueFormFields(form, mergedVenue, lockedCity);
        }

        if (lockedCity) {
          setTbdCity(lockedCity);
          if (id) setTbdCityLockedInStorage(id, lockedCity);
        }

        setVenueTbd(yetTbd);
      }

      if (id) dispatch(checkForValidation({ name: "", check: false }));
      if (user.new_user) dispatch(logged({ ...user, new_user: false }));

      const mergedVenueForStore = !isVirtual
        ? mergeVenueFromSave(res.data.venue, valueToSend.venue)
        : undefined;

      dispatch(
        setSelectedEventList({
          ...res.data,
          yet_to_be_announced:
            res.data.yet_to_be_announced ?? valueToSend.yet_to_be_announced,
          ...(mergedVenueForStore ? { venue: mergedVenueForStore } : {}),
        }),
      );

      if (shouldNavigate && res?.data?.id) {
        message.success("Event saved successfully");
        navigate(ROUTES.ADD_EVENT_DETAILS.path.replace(":id", res?.data?.id));
      } else {
        message.success("Saved as draft");
      }
    } else {
      message.error(res?.message);
    }
  };

  useEffect(() => {
    if (fetchedEvent?.status && fetchedEvent.data) {
      const fetchedVenue = resolveVenueFromEvent(fetchedEvent.data);
      dispatch(
        setSelectedEventList({
          ...fetchedEvent.data,
          ...(fetchedVenue ? { venue: fetchedVenue } : {}),
        }),
      );
    }
  }, [fetchedEvent, dispatch]);

  useLayoutEffect(() => {
    if (id && selectedEvent && selectedEvent.id) {
      if (hydratedEventIdRef.current !== selectedEvent.id) {
        hydratedEventIdRef.current = selectedEvent.id;
        const data: EventData = { ...selectedEvent };
        const loadedVenue = resolveVenueFromEvent(selectedEvent);
        setVenue(loadedVenue);

        const yetTbd = isYetToBeAnnounced(selectedEvent?.yet_to_be_announced);
        const lockedCity = resolveLockedTbdCity(
          selectedEvent.id,
          yetTbd,
          loadedVenue,
        );

        setVenueTbd(yetTbd);
        setTbdCity(lockedCity);
        const storedType = (selectedEvent as Record<string, unknown>)
          .event_type;
        if (typeof storedType === "string") setEventType(storedType);
        form.setFieldsValue({
          ...convertToDayjsForForm(data),
          event_type: (storedType as string) || "on_ground",
        });
        syncVenueFormFields(form, loadedVenue, lockedCity);
      }
    }
  }, [selectedEvent, id, form, dispatch]);

  if (error || fetchError) {
    message.error("Something went wrong");
    return null;
  }
  if (fetchLoading || fetchingEvent) return <Loader />;

  return (
    <Loader spinning={loading}>
      <div className="p-24">
        {/* ── Page Header ── */}
        <div className="flex items-center gap-24 mb-24">
          <Button
            icon={<IconArrowNarrowLeft size={18} />}
            shape="circle"
            variant="filled"
            color="default"
            className="border! shrink-0"
            onClick={() => navigate("/")}
          />
          <div>
            <h2 className="text-[20px] font-semibold text-[#111827] leading-tight">
              {id ? "Edit Event" : "Create New Event"}
            </h2>
            <p className="text-[13px] text-[#6b7280] mt-2">
              {id
                ? "Update your event's basic details and schedule"
                : "Set up the basic details for your new event"}
            </p>
          </div>
        </div>

        <div className="flex justify-center max-w-3xl mx-auto">
          <Form
            onFinish={onSubmit}
            form={form}
            layout="vertical"
            scrollToFirstError
            style={{ width: "100%" }}
            disabled={isFormDisabled}
          >
            {/* ── Fieldset 1: non-venue sections (locked for non-draft) ── */}
            <fieldset
              disabled={!canEdit}
              style={{ border: "none", padding: 0, margin: 0 }}
            >
              {/* ── Section: Event Basics ── */}
              <SectionCard
                title="Event Information"
                description="Name your event, select format and set visibility"
              >
                <Form.Item
                  name="name"
                  label="Event Name"
                  rules={[
                    { required: true, message: "This field is required" },
                    {
                      validator: (_, value) => {
                        if (!value || value.length < 10)
                          return Promise.reject(
                            "Name must be at least 10 characters",
                          );
                        if (value.length > 100)
                          return Promise.reject(
                            "Name must be at most 100 characters",
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                  validateFirst
                >
                  <Input
                    size="large"
                    count={{ show: true, max: 100 }}
                    placeholder="e.g. Mumbai Marathon 2025"
                    disabled={!canEdit}
                  />
                </Form.Item>
              </SectionCard>

              {/* ── Section: Description ── */}
              <SectionCard
                title="Event Description"
                description="Tell participants what to expect from your event"
              >
                <DescriptionArea
                  MAX_LENGTH={5000}
                  disabled={!canEditEventSetup(selectedEvent)}
                />
                <Form.Item
                  label="Event Type"
                  required
                  name="event_type"
                  initialValue="on_ground"
                  style={{ marginBottom: 16 }}
                >
                  <EventModeSelector
                    value={eventType}
                    onChange={(key) => {
                      setEventType(key);
                      form.setFieldValue("event_type", key);
                      if (key === "virtual") form.resetFields(["virtual_link"]);
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="visiblity"
                  label="Visibility"
                  initialValue="public"
                  required
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group disabled={!canEdit}>
                    <Radio value="public">Public</Radio>
                    <Radio value="private">Private</Radio>
                  </Radio.Group>
                </Form.Item>
              </SectionCard>
            </fieldset>

            {/* ── Section: Schedule — outside locked fieldset so published events without bookings can edit dates ── */}
            <SectionCard
              title="Event Schedule"
              description="Set when your event starts and ends"
            >
              <Row gutter={[16, 0]}>
                <DateTimePicker
                  form={form}
                  label={["Start Date", "Start Time", "End Date", "End Time"]}
                  name={["start_date", "start_time", "end_date", "end_time"]}
                  disable={!canEditScheduleVenue}
                  savedSchedule={
                    canEdit && id && selectedEvent?.start_date
                      ? {
                          start_date: selectedEvent.start_date,
                          start_time: selectedEvent.start_time,
                          end_date: selectedEvent.end_date,
                          end_time: selectedEvent.end_time,
                        }
                      : undefined
                  }
                />
              </Row>
            </SectionCard>

            {/* ── Section: Venue — OUTSIDE disabled fieldset ── */}
            {!isVirtual && (
              <SectionCard
                title="Event Venue"
                description="Set the location where your event will take place"
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <VenuConfirmationEvent
                      show={venueTbd}
                      setShow={setVenueTbd}
                      scheduleVenueLocked={scheduleAndVenueLocked}
                      isLocked={isRequested}
                    />
                  </Col>
                  <Col xs={24}>
                    <Venue
                      mode={
                        venueTbd && !canEditScheduleVenue ? "city-pick" : "normal"
                      }
                      venue={venue}
                      setVenue={setVenue}
                      form={form}
                      scheduleVenueLocked={scheduleAndVenueLocked}
                      venueLocked={venueLocked}
                    />
                  </Col>
                </Row>
              </SectionCard>
            )}

            {/* ── Fieldset 2: virtual meeting (locked for non-draft) ── */}
            <fieldset
              disabled={!canEdit}
              style={{ border: "none", padding: 0, margin: 0 }}
            >
              {/* ── Section: Virtual Meeting (Virtual & Hybrid) ── */}
              {(eventType === "virtual" || eventType === "hybrid") && (
                <SectionCard
                  title="Virtual Meeting"
                  description="Provide the online meeting link for participants"
                >
                  <Form.Item
                    name="virtual_link"
                    label="Meeting Link"
                    rules={[
                      { required: true, message: "Meeting link is required" },
                      { type: "url", message: "Please enter a valid URL" },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      size="large"
                      placeholder="https://meet.example.com/your-event"
                      prefix={<GlobalOutlined style={{ color: "#8b5cf6" }} />}
                      disabled={!canEdit}
                    />
                  </Form.Item>
                </SectionCard>
              )}
            </fieldset>

            {/* ── Footer Actions ── */}
            <div className="flex justify-between items-center pt-4 pb-8">
              <div>
                {!id && (
                  <Button
                    onClick={() => {
                      navigate(ROUTES.LIST_EVENT.path);
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={isFormDisabled}
                  iconPosition="end"
                >
                  {!id ? "Save & Continue" : "Update & Continue"}
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </Loader>
  );
};

export default AddEvents;
